import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import {
  PublisherCostType,
  PublisherCpmType,
  PublisherStatus,
  PublisherXpParameter,
  type Publisher,
  type PublisherMutationPayload,
} from './model';

const required = z.string().trim().min(1, dashboardT('publishers.validation.required'));
const optionalCostType = z.union([z.enum(PublisherCostType), z.literal('')]);
const optionalCpmType = z.union([z.enum(PublisherCpmType), z.literal('')]);

const publisherFormValueSchema = z.object({
  name: required,
  status: z.enum(PublisherStatus),
  notes: z.string(),
  companyName: required,
  contactName: required,
  phone: z.string(),
  email: required.pipe(z.email(dashboardT('publishers.validation.email'))),
  websiteUrl: required.regex(
    /^(?!.*\bhttps?:\/\/\b)([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
    dashboardT('publishers.validation.website'),
  ),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
  country: z.string(),
  gamIdEnabled: z.boolean(),
  gamAdUnitId: z.string(),
  mcmActive: z.boolean(),
  schainAsi: z.string(),
  schainSid: z.string(),
  rewritePublisherId: z.boolean(),
  alternativePublisherId: z.string(),
  revenueCostType: optionalCostType,
  revenueShare: z.string(),
  revenueCpmValue: z.string(),
  revenueShareIo: z.string(),
  saasCostType: optionalCostType,
  saasCpmMetric: optionalCpmType,
  saasCpmValue: z.string(),
  saasRevenueShare: z.string(),
  whitelistAppsEnabled: z.boolean(),
  whitelistDomainsEnabled: z.boolean(),
  xpParametersMappings: z
    .array(
      z.object({
        value: z.string(),
        xpParameter: z.union([z.enum(PublisherXpParameter), z.literal('')]),
      }),
    )
    .max(10),
});

export function createPublisherFormSchema({
  campaignFeatureAllowed = true,
}: PublisherFormSchemaOptions = {}) {
  return publisherFormValueSchema.superRefine((value, context) => {
    if (campaignFeatureAllowed && value.gamIdEnabled && !value.gamAdUnitId.trim()) {
      addRequiredIssue(context, ['gamAdUnitId']);
    }
    if (value.rewritePublisherId && !value.alternativePublisherId.trim()) {
      addRequiredIssue(context, ['alternativePublisherId']);
    }
    if (value.revenueCostType === PublisherCostType.RevenueShare) {
      validatePercentage(value.revenueShare, ['revenueShare'], context, true);
      validatePercentage(value.revenueShareIo, ['revenueShareIo'], context, false);
    }
    if (value.revenueCostType === PublisherCostType.Cpm && value.revenueCpmValue.trim()) {
      validateAmount(value.revenueCpmValue, ['revenueCpmValue'], context);
    }
    if (value.saasCostType === PublisherCostType.RevenueShare) {
      validatePercentage(value.saasRevenueShare, ['saasRevenueShare'], context, true);
    }
    if (value.saasCostType === PublisherCostType.Cpm) {
      if (!value.saasCpmMetric) addRequiredIssue(context, ['saasCpmMetric']);
      const amount = Number(value.saasCpmValue);
      if (!value.saasCpmValue.trim() || !Number.isFinite(amount) || amount < 0) {
        context.addIssue({
          code: 'custom',
          path: ['saasCpmValue'],
          message: dashboardT('publishers.validation.amount'),
        });
      }
    }
    value.xpParametersMappings.forEach((mapping, index) => {
      const hasValue = Boolean(mapping.value.trim());
      const hasParameter = Boolean(mapping.xpParameter);
      if (hasValue === hasParameter) return;
      addRequiredIssue(context, [
        'xpParametersMappings',
        index,
        hasValue ? 'xpParameter' : 'value',
      ]);
    });
    const selectedMappings = value.xpParametersMappings
      .map((mapping) => mapping.xpParameter)
      .filter(Boolean);
    if (new Set(selectedMappings).size !== selectedMappings.length) {
      context.addIssue({
        code: 'custom',
        path: ['xpParametersMappings'],
        message: dashboardT('publishers.validation.uniqueXp'),
      });
    }
  });
}

export const publisherFormSchema = createPublisherFormSchema();

export type PublisherFormValue = z.infer<typeof publisherFormValueSchema>;

interface PublisherFormSchemaOptions {
  campaignFeatureAllowed?: boolean;
}

export function emptyPublisherFormValue(): PublisherFormValue {
  return {
    name: '',
    status: PublisherStatus.Active,
    notes: '',
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    websiteUrl: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    gamIdEnabled: false,
    gamAdUnitId: '',
    mcmActive: false,
    schainAsi: '',
    schainSid: '',
    rewritePublisherId: false,
    alternativePublisherId: '',
    revenueCostType: '',
    revenueShare: '',
    revenueCpmValue: '',
    revenueShareIo: '',
    saasCostType: '',
    saasCpmMetric: '',
    saasCpmValue: '',
    saasRevenueShare: '',
    whitelistAppsEnabled: false,
    whitelistDomainsEnabled: false,
    xpParametersMappings: [],
  };
}

export function publisherToFormValue(publisher: Publisher): PublisherFormValue {
  return {
    ...emptyPublisherFormValue(),
    name: publisher.name,
    status: publisher.status,
    notes: publisher.notes ?? '',
    companyName: publisher.companyName ?? '',
    contactName: publisher.contactName ?? '',
    phone: publisher.phone ?? '',
    email: publisher.email ?? '',
    websiteUrl: publisher.websiteUrl ?? '',
    address: publisher.address ?? '',
    city: publisher.city ?? '',
    state: publisher.state ?? '',
    zipCode: publisher.zipCode ?? '',
    country: publisher.country ?? '',
    gamIdEnabled: publisher.gamIdEnabled ?? false,
    gamAdUnitId: publisher.gamAdUnitId ?? '',
    mcmActive: publisher.mcmActive ?? false,
    schainAsi: publisher.schainAsi ?? '',
    schainSid: publisher.schainSid ?? '',
    rewritePublisherId: publisher.rewritePublisherId ?? false,
    alternativePublisherId: publisher.alternativePublisherId ?? '',
    revenueCostType: publisher.allProductsCost?.costType ?? '',
    revenueShare: percentString(
      publisher.allProductsCost?.costType === PublisherCostType.RevenueShare
        ? publisher.allProductsCost.value
        : null,
    ),
    revenueCpmValue:
      publisher.allProductsCost?.costType === PublisherCostType.Cpm
        ? String(publisher.allProductsCost.value ?? '')
        : '',
    revenueShareIo: percentString(publisher.revenueShareIo),
    saasCostType: publisher.saasCost?.costType ?? '',
    saasCpmMetric: publisher.saasCost?.cpmMetric ?? '',
    saasCpmValue:
      publisher.saasCost?.costType === PublisherCostType.Cpm
        ? String(publisher.saasCost.value ?? '')
        : '',
    saasRevenueShare: percentString(
      publisher.saasCost?.costType === PublisherCostType.RevenueShare
        ? publisher.saasCost.value
        : null,
    ),
    whitelistAppsEnabled: publisher.whitelistAppsEnabled ?? false,
    whitelistDomainsEnabled: publisher.whitelistDomainsEnabled ?? false,
    xpParametersMappings: publisher.xpParametersMappings ?? [],
  };
}

export function serializePublisherFormValue(value: PublisherFormValue): PublisherMutationPayload {
  const allProductsCost = value.revenueCostType
    ? {
        costType: value.revenueCostType,
        value: resolveAllProductsCostValue(value),
      }
    : null;
  const saasCost = value.saasCostType
    ? {
        costType: value.saasCostType,
        value:
          value.saasCostType === PublisherCostType.Cpm
            ? Number(value.saasCpmValue)
            : percentageFraction(value.saasRevenueShare),
        cpmMetric:
          value.saasCostType === PublisherCostType.Cpm && value.saasCpmMetric
            ? value.saasCpmMetric
            : null,
      }
    : null;
  return {
    name: value.name.trim(),
    status: value.status,
    notes: value.notes.trim(),
    companyName: value.companyName.trim(),
    contactName: value.contactName.trim(),
    phone: value.phone.trim(),
    email: value.email.trim(),
    websiteUrl: value.websiteUrl.trim(),
    address: value.address.trim(),
    city: value.city.trim(),
    state: value.state.trim(),
    zipCode: value.zipCode.trim(),
    country: value.country.trim(),
    gamIdEnabled: value.gamIdEnabled,
    gamAdUnitId: value.gamIdEnabled ? value.gamAdUnitId.trim() : '',
    mcmActive: value.mcmActive,
    schainAsi: value.schainAsi.trim(),
    schainSid: value.schainSid.trim(),
    rewritePublisherId: value.rewritePublisherId,
    alternativePublisherId: value.rewritePublisherId ? value.alternativePublisherId.trim() : '',
    revenueShareIo:
      value.revenueCostType === PublisherCostType.RevenueShare && value.revenueShareIo.trim()
        ? percentageFraction(value.revenueShareIo)
        : null,
    allProductsCost,
    saasCost,
    whitelistAppsEnabled: value.whitelistAppsEnabled,
    whitelistDomainsEnabled: value.whitelistDomainsEnabled,
    xpParametersMappings: value.xpParametersMappings.filter(
      (mapping): mapping is { value: string; xpParameter: PublisherXpParameter } =>
        Boolean(mapping.value.trim() && mapping.xpParameter),
    ),
  };
}

function validatePercentage(
  input: string,
  path: (string | number)[],
  context: z.RefinementCtx,
  requiredValue: boolean,
) {
  if (!input.trim() && !requiredValue) return;
  const value = Number(input);
  if (!input.trim() || !Number.isFinite(value) || value < 0 || value > 100) {
    context.addIssue({
      code: 'custom',
      path,
      message: dashboardT('publishers.validation.percent'),
    });
  }
}

function addRequiredIssue(context: z.RefinementCtx, path: (string | number)[]) {
  context.addIssue({ code: 'custom', path, message: dashboardT('publishers.validation.required') });
}

function validateAmount(input: string, path: (string | number)[], context: z.RefinementCtx) {
  const amount = Number(input);
  if (!Number.isFinite(amount) || amount < 0) {
    context.addIssue({
      code: 'custom',
      path,
      message: dashboardT('publishers.validation.amount'),
    });
  }
}

function optionalAmount(value: string) {
  return value.trim() ? Number(value) : null;
}

function resolveAllProductsCostValue(value: PublisherFormValue) {
  switch (value.revenueCostType) {
    case PublisherCostType.Cpm:
      return optionalAmount(value.revenueCpmValue);
    case PublisherCostType.RevenueShare:
      return percentageFraction(value.revenueShare);
    case PublisherCostType.Placement:
    case '':
      return null;
  }
}

function percentageFraction(value: string) {
  return Number(value) / 100;
}

function percentString(value: number | null | undefined) {
  return value == null ? '' : String(value * 100);
}
